/* ==========================================================================================
   tk.js — behaviour for the tk-* layer: header, nav drawer, reveal-on-scroll, scroll-driven hero,
   console recolor, counters, marquee, feature film, colorway picker, accordion, product
   configurator (step funnel), sticky buy bar, AJAX cart drawer, toast.
   ========================================================================================== */
(function () {
  'use strict';
  const TK = (window.TK = window.TK || {});
  TK.routes = TK.routes || { root: '/', cart: '/cart', checkout: '/checkout' };
  TK.settings = TK.settings || { motion: 'on', cart: 'drawer' };
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.prototype.slice.call((r || document).querySelectorAll(s));
  const on = (el, ev, fn, o) => el && el.addEventListener(ev, fn, o);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => t * t * (3 - 2 * t);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOTION = TK.settings.motion === 'off' || reduced ? 'off' : TK.settings.motion === 'reduced' ? 'reduced' : 'on';
  if (MOTION === 'off') document.documentElement.classList.add('tk-no-motion');
  const money = (cents) => {
    const fmt = TK.moneyFormat || '${{amount}}';
    const amount = (Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  };
  TK.money = money;

  /* ---------- toast ---------- */
  let toastEl, toastT;
  TK.toast = function (msg, ms) {
    if (!toastEl) { toastEl = document.createElement('div'); toastEl.className = 'tk-toast'; toastEl.setAttribute('role', 'status'); document.body.appendChild(toastEl); }
    toastEl.textContent = msg; toastEl.classList.add('is-on'); clearTimeout(toastT);
    toastT = setTimeout(() => toastEl.classList.remove('is-on'), ms || 2200);
  };

  /* ---------- header ---------- */
  function initHeader() {
    const h = $('[data-tk-header]');
    if (!h) return;
    const hideOnScroll = h.dataset.hide === 'true';
    let last = window.scrollY, ticking = false;
    const update = () => {
      const y = window.scrollY;
      h.classList.toggle('is-scrolled', y > 24);
      if (hideOnScroll) h.classList.toggle('is-hidden', y > last + 6 && y > 200), y < last - 6 && h.classList.remove('is-hidden');
      last = y; ticking = false;
    };
    on(window, 'scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
    const nav = $('[data-tk-nav]');
    const open = () => { if (!nav) return; nav.classList.add('is-open'); nav.setAttribute('aria-hidden', 'false'); document.body.classList.add('tk-lock'); $$('[data-tk-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'true')); };
    const close = () => { if (!nav) return; nav.classList.remove('is-open'); nav.setAttribute('aria-hidden', 'true'); document.body.classList.remove('tk-lock'); $$('[data-tk-nav-open]').forEach((b) => b.setAttribute('aria-expanded', 'false')); };
    TK.closeNav = close;
    on(document, 'click', (e) => {
      if (e.target.closest('[data-tk-nav-open]')) { e.preventDefault(); open(); }
      if (e.target.closest('[data-tk-nav-close]') || e.target.closest('[data-tk-nav-backdrop]')) close();
      const a = e.target.closest('[data-tk-nav] a'); if (a) close();
    });
    on(document, 'keydown', (e) => e.key === 'Escape' && close());
    // active anchor highlighting
    const links = $$('.tk-header__nav a[href*="#"]');
    const targets = links.map((a) => { const id = a.getAttribute('href').split('#')[1]; return id ? { a, el: document.getElementById(id) } : null; }).filter((t) => t && t.el);
    if (targets.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => { entries.forEach((en) => { const t = targets.find((x) => x.el === en.target); if (t && en.isIntersecting) { links.forEach((l) => l.classList.remove('is-active')); t.a.classList.add('is-active'); } }); }, { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach((t) => io.observe(t.el));
    }
  }

  /* ---------- reveal on scroll ---------- */
  function initReveal(root) {
    const els = $$('.tk-reveal:not(.is-in)', root);
    if (!els.length) return;
    if (MOTION === 'off' || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => { entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }); }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- counters ---------- */
  function initCounters(root) {
    const els = $$('[data-tk-count]', root);
    if (!els.length) return;
    const run = (el) => {
      if (el._done) return; el._done = true;
      const target = parseFloat(el.dataset.tkCount), dec = Number(el.dataset.decimals || 0), dur = Number(el.dataset.duration || 1600);
      const sep = el.dataset.sep !== 'false';
      const fmt = (v) => { let s = v.toFixed(dec); if (sep) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ','); return s; };
      if (MOTION === 'off') { el.textContent = fmt(target); return; }
      const t0 = performance.now();
      const step = (t) => { const p = clamp((t - t0) / dur, 0, 1); const e = 1 - Math.pow(1 - p, 3); el.textContent = fmt(target * e); if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) { els.forEach(run); return; }
    const io = new IntersectionObserver((entries) => { entries.forEach((en) => { if (en.isIntersecting) { run(en.target); io.unobserve(en.target); } }); }, { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- console helpers ---------- */
  TK.setShell = function (consoleEl, hex) {
    if (!consoleEl) return;
    consoleEl.style.setProperty('--shell', hex);
    const b = brightness(hex);
    $$('[data-ink]', consoleEl).forEach((t) => t.setAttribute('fill', b > 150 ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.55)'));
    $$('[data-ink-soft]', consoleEl).forEach((t) => t.setAttribute('fill', b > 150 ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.18)'));
  };
  function brightness(hex) { const h = String(hex || '').replace('#', ''); const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h; const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16); return (r * 299 + g * 587 + b * 114) / 1000; }
  TK.screen = function (consoleEl, state) { if (!consoleEl) return; consoleEl.classList.remove('is-on', 'is-off', 'is-boot'); consoleEl.classList.add('is-' + state); };

  /* ---------- scroll hero ---------- */
  function kf(frames, p) {
    // frames: [[p, value], ...] sorted; smoothstep interpolation between neighbours
    if (p <= frames[0][0]) return frames[0][1];
    for (let i = 1; i < frames.length; i++) {
      if (p <= frames[i][0]) { const [p0, v0] = frames[i - 1], [p1, v1] = frames[i]; return lerp(v0, v1, smooth((p - p0) / (p1 - p0))); }
    }
    return frames[frames.length - 1][1];
  }
  const win = (p, a, b, fade) => { fade = fade || 0.05; if (p < a || p > b) return 0; if (p < a + fade) return smooth((p - a) / fade); if (p > b - fade) return smooth((b - p) / fade); return 1; };

  function initHero() {
    const hero = $('[data-tk-hero]');
    if (!hero) return;
    const stage = $('.tk-hero__stage', hero), device = $('[data-hero-device]', hero), consoleEl = $('[data-tk-console]', hero);
    if (MOTION === 'off' || hero.classList.contains('tk-hero--static')) { hero.classList.add('tk-hero--static'); TK.screen(consoleEl, 'on'); return; }
    const copies = $$('[data-phase]', hero);
    const labels = $$('.tk-hero__label', hero);
    const dots = $$('.tk-hero__dots li', hero);
    const swatches = $$('.tk-hero__swatch', hero);
    const swName = $('.tk-hero__swatch-name', hero);
    const hint = $('.tk-hero__hint', hero);
    const isMobile = () => window.innerWidth < 750;
    const colors = swatches.map((s) => ({ hex: s.dataset.hex, name: s.dataset.name }));
    const baseHex = consoleEl ? getComputedStyle(consoleEl).getPropertyValue('--shell').trim() : '#1a1a1e';
    // phase windows (fractions of the track)
    const P = { intro: [0, 0.16], boot: [0.16, 0.34], tilt: [0.34, 0.5], explode: [0.5, 0.72], colors: [0.72, 0.88], cta: [0.88, 1.0] };
    const F = {
      s: [[0, isMobile() ? 0.8 : 0.72], [0.18, 0.96], [0.34, 0.96], [0.5, 0.9], [0.72, 0.9], [0.8, 0.96], [0.9, 0.62], [1, 0.62]],
      y: [[0, 46], [0.18, 6], [0.5, 2], [0.72, 2], [0.84, 4], [0.9, -20], [1, -20]],
      rx: [[0.3, 0], [0.42, 56], [0.68, 56], [0.8, 0]],
      rz: [[0.3, 0], [0.42, -16], [0.68, -16], [0.8, 0]],
      ry: [[0.3, 0], [0.42, 8], [0.68, 8], [0.8, 0]],
      ex: [[0.44, 0], [0.56, 1], [0.66, 1], [0.76, 0]],
      grid: [[0, 0.7], [0.16, 0.35], [0.34, 0.6], [0.72, 0.6], [0.88, 0.15]],
    };
    let lastState = '', lastColor = -1, ticking = false, p = 0;
    const update = () => {
      ticking = false;
      const rect = hero.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height - vh;
      p = clamp(-rect.top / Math.max(total, 1), 0, 1);
      const m = isMobile();
      device.style.setProperty('--dev-s', kf(F.s, p).toFixed(4));
      device.style.setProperty('--dev-y', (kf(F.y, p) * (m ? 0.8 : 1)).toFixed(3) + 'vh');
      device.style.setProperty('--dev-rx', kf(F.rx, p).toFixed(2) + 'deg');
      device.style.setProperty('--dev-ry', kf(F.ry, p).toFixed(2) + 'deg');
      device.style.setProperty('--dev-rz', kf(F.rz, p).toFixed(2) + 'deg');
      device.style.setProperty('--dev-ex', kf(F.ex, p).toFixed(4));
      stage.style.setProperty('--grid-o', kf(F.grid, p).toFixed(3));
      // screen state
      let state = 'off';
      if (p >= 0.19 && p < 0.25) state = 'boot'; else if (p >= 0.25) state = 'on';
      if (state !== lastState) { TK.screen(consoleEl, state); lastState = state; }
      // copy visibility
      copies.forEach((el) => {
        const ph = el.dataset.phase; const w = P[ph]; if (!w) return;
        const o = ph === 'intro' ? (p < w[1] ? 1 - smooth(clamp(p / (w[1] * 0.7), 0, 1)) : 0) : ph === 'cta' ? smooth(clamp((p - w[0]) / 0.06, 0, 1)) : win(p, w[0], w[1], 0.05);
        el.style.setProperty('--c-o', o.toFixed(3));
        el.style.setProperty('--c-y', ((1 - o) * (ph === 'intro' ? -40 : 24)).toFixed(2) + 'px');
        el.classList.toggle('is-active', o > 0.5);
      });
      // labels appear one by one during explode
      labels.forEach((l, i) => { const a = 0.53 + i * 0.035; l.style.setProperty('--l-o', win(p, a, 0.72, 0.03).toFixed(3)); });
      // colorway sweep
      if (colors.length) {
        let idx = -1;
        if (p >= P.colors[0] && p < P.colors[1]) idx = Math.min(colors.length - 1, Math.floor(((p - P.colors[0]) / (P.colors[1] - P.colors[0])) * colors.length));
        if (idx !== lastColor) {
          lastColor = idx;
          const c = idx >= 0 ? colors[idx] : null;
          TK.setShell(consoleEl, c ? c.hex : baseHex);
          swatches.forEach((s, i) => s.classList.toggle('is-active', i === idx));
          if (swName) swName.textContent = c ? c.name : '';
          if (idx === -1 && p >= P.colors[1]) { const last = colors[colors.length - 1]; TK.setShell(consoleEl, last.hex); }
        }
      }
      // dots
      const phases = Object.keys(P); const cur = phases.findIndex((k) => p >= P[k][0] && p < P[k][1]);
      dots.forEach((d, i) => d.classList.toggle('is-active', i === (cur < 0 ? phases.length - 1 : cur)));
      if (hint) hint.style.setProperty('--hint-o', p < 0.04 ? 1 : 0);
    };
    on(window, 'scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    on(window, 'resize', update);
    update();
    // keyboard: let the device be tabbable to the buy button
  }

  /* ---------- marquee (duplicates content for seamless loop) ---------- */
  function initMarquee(root) {
    $$('[data-tk-marquee]', root).forEach((m) => {
      if (m._done) return; m._done = true;
      const track = $('.tk-marquee__track', m); if (!track) return;
      const clone = track.cloneNode(true); clone.setAttribute('aria-hidden', 'true'); m.appendChild(clone);
    });
  }

  /* ---------- feature film (pinned storytelling) ---------- */
  function initFilm(root) {
    $$('[data-tk-film]', root).forEach((film) => {
      const steps = $$('[data-film-step]', film), consoleEl = $('[data-tk-console]', film), stageCopy = $$('[data-film-screen]', film);
      if (!steps.length) return;
      const activate = (i) => {
        steps.forEach((s, j) => s.classList.toggle('is-active', i === j));
        const st = steps[i];
        if (consoleEl && st.dataset.shell) TK.setShell(consoleEl, st.dataset.shell);
        if (consoleEl && st.dataset.screen) TK.screen(consoleEl, st.dataset.screen);
        film.dataset.active = String(i);
        stageCopy.forEach((c, j) => c.classList.toggle('is-active', i === j));
        $$('[data-film-dot]', film).forEach((d, j) => d.classList.toggle('is-active', i === j));
      };
      activate(0);
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => { entries.forEach((en) => { if (en.isIntersecting) activate(steps.indexOf(en.target)); }); }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      steps.forEach((s) => io.observe(s));
    });
  }

  /* ---------- colorway picker section ---------- */
  function initColors(root) {
    $$('[data-tk-colors]', root).forEach((sec) => {
      const consoleEl = $('[data-tk-console]', sec), btns = $$('[data-color-hex]', sec), name = $('[data-color-current]', sec), link = $('[data-color-link]', sec), big = $('[data-color-big]', sec);
      const pick = (b) => {
        btns.forEach((x) => { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        TK.setShell(consoleEl, b.dataset.colorHex);
        if (name) name.textContent = b.dataset.colorName;
        if (big) { big.textContent = b.dataset.colorName; big.style.setProperty('--c', b.dataset.colorHex); }
        if (link && b.dataset.colorUrl) link.setAttribute('href', b.dataset.colorUrl);
      };
      btns.forEach((b) => on(b, 'click', () => pick(b)));
      if (btns[0]) pick(btns[0]);
      // auto-cycle until the user interacts
      if (MOTION === 'on' && sec.dataset.autoplay === 'true' && btns.length > 1) {
        let i = 0, t = setInterval(() => { i = (i + 1) % btns.length; pick(btns[i]); }, 2600);
        on(sec, 'click', () => clearInterval(t), { once: true });
      }
    });
  }

  /* ---------- accordion ---------- */
  function initAccordion(root) {
    $$('.tk-acc__btn', root).forEach((btn) => {
      if (btn._bound) return; btn._bound = true;
      on(btn, 'click', () => {
        const item = btn.closest('.tk-acc__item'), acc = btn.closest('.tk-acc');
        const open = item.classList.contains('is-open');
        if (acc && acc.dataset.single === 'true') $$('.tk-acc__item', acc).forEach((it) => { it.classList.remove('is-open'); $('.tk-acc__btn', it).setAttribute('aria-expanded', 'false'); });
        item.classList.toggle('is-open', !open); btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }

  /* ---------- tilt cards ---------- */
  function initTilt(root) {
    if (MOTION !== 'on' || window.matchMedia('(hover: none)').matches) return;
    $$('[data-tk-tilt]', root).forEach((card) => {
      if (card._tilt) return; card._tilt = true;
      on(card, 'pointermove', (e) => { const r = card.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5; card.style.transform = `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg) translateY(-4px)`; card.style.setProperty('--mx', (x * 100 + 50).toFixed(1) + '%'); card.style.setProperty('--my', (y * 100 + 50).toFixed(1) + '%'); });
      on(card, 'pointerleave', () => { card.style.transform = ''; });
    });
  }

  /* ==========================================================================================
     CART
     ========================================================================================== */
  const cart = (TK.cart = {
    sectionId: 'tk-cart-drawer', busy: false,
    get drawer() { return $('[data-tk-cart-drawer]'); },
    open() { const d = this.drawer; if (!d) return; d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); document.body.classList.add('tk-lock'); const c = $('[data-cart-close]', d); c && c.focus({ preventScroll: true }); document.dispatchEvent(new CustomEvent('tk:cart:open')); },
    close() { const d = this.drawer; if (!d) return; d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); document.body.classList.remove('tk-lock'); },
    setCount(n) { $$('[data-cart-count]').forEach((el) => { el.textContent = n; el.dataset.count = n; }); },
    async request(url, body) {
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong');
      return data;
    },
    render(sections) {
      const html = sections && sections[this.sectionId]; const d = this.drawer; if (!html || !d) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-inner]', doc), cur = $('[data-cart-inner]', d);
      if (fresh && cur) { cur.replaceWith(fresh); this.bind(); }
    },
    async refresh() {
      try {
        const res = await fetch(`${TK.routes.root || '/'}?sections=${this.sectionId}`, { headers: { Accept: 'application/json' } });
        this.render(await res.json());
        const c = await fetch(`${TK.routes.cart || '/cart'}.js`).then((r) => r.json());
        this.setCount(c.item_count);
      } catch (e) { console.warn('[tk] cart refresh failed', e); }
    },
    async add(items, { open = true, button, silent = false } = {}) {
      if (this.busy) return; this.busy = true; button && button.classList.add('is-loading');
      try {
        if (TK.harness) { await new Promise((r) => setTimeout(r, 500)); TK.toast('Harness: would add ' + JSON.stringify(items)); return; }
        const data = await this.request(`${TK.routes.cart || '/cart'}/add.js`, { items, sections: this.sectionId });
        const c = await fetch(`${TK.routes.cart || '/cart'}.js`).then((r) => r.json());
        this.setCount(c.item_count); this.render(data.sections);
        document.dispatchEvent(new CustomEvent('tk:cart:added', { detail: { items, cart: c } }));
        if (open) { if (this.drawer && TK.settings.cart !== 'page') this.open(); else window.location.href = TK.routes.cart || '/cart'; } else if (!silent) TK.toast('Added to bag');
        return c;
      } catch (e) { TK.toast(e.message || 'Could not add to bag', 3200); throw e; } finally { this.busy = false; button && button.classList.remove('is-loading'); }
    },
    async change(key, quantity, lineEl) {
      lineEl && lineEl.classList.add('is-loading');
      try {
        const data = await this.request(`${TK.routes.cart || '/cart'}/change.js`, { id: key, quantity, sections: this.sectionId });
        this.setCount(data.item_count); this.render(data.sections);
        if ($('[data-tk-cart-page]')) window.location.reload();
        document.dispatchEvent(new CustomEvent('tk:cart:changed', { detail: { cart: data } }));
      } catch (e) { TK.toast(e.message || 'Could not update bag', 3200); lineEl && lineEl.classList.remove('is-loading'); }
    },
    bind() {
      const scope = this.drawer || document;
      $$('[data-cart-close]', scope).forEach((b) => (b.onclick = () => this.close()));
      $$('[data-line-key]', document).forEach((line) => {
        if (line._bound) return; line._bound = true;
        const key = line.dataset.lineKey, input = $('[data-qty-input]', line);
        const val = () => Math.max(0, parseInt(input && input.value, 10) || 0);
        on($('[data-qty-minus]', line), 'click', () => this.change(key, val() - 1, line));
        on($('[data-qty-plus]', line), 'click', () => this.change(key, val() + 1, line));
        on(input, 'change', () => this.change(key, val(), line));
        on($('[data-line-remove]', line), 'click', (e) => { e.preventDefault(); this.change(key, 0, line); });
      });
    },
  });
  function initCart() {
    cart.bind();
    on(document, 'click', (e) => {
      const openBtn = e.target.closest('[data-tk-cart-open]');
      if (openBtn && cart.drawer && TK.settings.cart !== 'page') { e.preventDefault(); cart.open(); return; }
      if (e.target.closest('[data-cart-backdrop]')) cart.close();
      const add = e.target.closest('[data-tk-add]');
      if (add) { e.preventDefault(); const id = Number(add.dataset.tkAdd), qty = Number(add.dataset.qty || 1); if (id) cart.add([{ id, quantity: qty }], { button: add, open: add.dataset.open !== 'false' }); }
      const scroll = e.target.closest('[data-tk-scroll]');
      if (scroll) { const id = (scroll.getAttribute('href') || '').split('#')[1]; const el = id && document.getElementById(id); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: MOTION === 'off' ? 'auto' : 'smooth', block: 'start' }); } }
    });
    on(document, 'keydown', (e) => e.key === 'Escape' && cart.close());
    on(document, 'shopify:section:select', (e) => { if (e.target && e.target.querySelector && e.target.querySelector('[data-tk-cart-drawer]')) cart.open(); });
    on(document, 'shopify:section:deselect', (e) => { if (e.target && e.target.querySelector && e.target.querySelector('[data-tk-cart-drawer]')) cart.close(); });
    if (location.search.includes('cart=open')) cart.open();
  }

  /* ==========================================================================================
     PRODUCT CONFIGURATOR (step funnel)
     ========================================================================================== */
  function initConfigurator(root) {
    $$('[data-tk-config]', root).forEach((cfg) => {
      if (cfg._bound) return; cfg._bound = true;
      let variants = [];
      try { variants = JSON.parse($('[data-tk-variants]', cfg).textContent); } catch (e) { return; }
      const optionCount = Number(cfg.dataset.options || 1);
      const steps = $$('[data-step]', cfg);
      const optionSteps = steps.filter((s) => s.dataset.step === 'option');
      const qtyStep = steps.find((s) => s.dataset.step === 'qty');
      const summary = $('[data-summary]', cfg);
      const form = $('form', cfg), idInput = $('input[name="id"]', cfg), qtyInput = $('input[name="quantity"]', cfg);
      const addBtn = $('[data-add]', cfg), buyNow = $('[data-buy-now]', cfg);
      const consoleEl = $('[data-tk-console]', cfg.closest('[data-tk-product]') || cfg) || $('[data-tk-console]');
      const stage = cfg.closest('[data-tk-product]') || document;
      const gallery = $('[data-gallery]', stage);
      const stickyBar = $('[data-sticky-bar]', stage);
      const colorOption = Number(cfg.dataset.colorOption || 0); // 1-based index of the color option, 0 = none
      const selected = new Array(optionCount).fill(null);
      let qty = Number((qtyInput && qtyInput.value) || 1);
      const progress = $('[data-progress]', cfg), progressText = $('[data-progress-text]', cfg);
      const totalSteps = optionSteps.length + (qtyStep ? 1 : 0);

      const findVariant = (sel) => variants.find((v) => sel.every((s, i) => s === null || v.options[i] === s));
      const cheapestFor = (sel) => { const list = variants.filter((v) => sel.every((s, i) => s === null || v.options[i] === s)); return list.length ? Math.min.apply(null, list.map((v) => v.price)) : null; };
      const availableFor = (sel) => variants.some((v) => v.available && sel.every((s, i) => s === null || v.options[i] === s));

      function setStepState(step, state) { step.dataset.state = state; const btn = $('[data-step-change]', step); if (btn) btn.hidden = state !== 'done'; }
      function openStep(i, scroll) {
        const all = optionSteps.concat(qtyStep ? [qtyStep] : []);
        all.forEach((s, j) => setStepState(s, j < i ? 'done' : j === i ? 'open' : 'locked'));
        if (progress) progress.style.setProperty('--pct', ((i / totalSteps) * 100).toFixed(1) + '%');
        if (progressText) progressText.textContent = `Step ${Math.min(i + 1, totalSteps)} of ${totalSteps}`;
        if (summary) summary.classList.toggle('is-open', i >= all.length);
        if (scroll && i < all.length && window.innerWidth < 750) { const top = all[i].getBoundingClientRect().top + window.scrollY - 120; if (Math.abs(window.scrollY - top) > 180) window.scrollTo({ top, behavior: MOTION === 'off' ? 'auto' : 'smooth' }); }
      }
      function refreshOptionStep(i) {
        const step = optionSteps[i];
        const base = selected.slice(); for (let k = i; k < base.length; k++) base[k] = null;
        const baseMin = cheapestFor(base);
        $$('[data-value]', step).forEach((card) => {
          const sel = base.slice(); sel[i] = card.dataset.value;
          const avail = availableFor(sel), min = cheapestFor(sel);
          card.classList.toggle('is-unavailable', !avail);
          card.setAttribute('aria-disabled', avail ? 'false' : 'true');
          const diff = $('[data-diff]', card);
          if (diff && min !== null && baseMin !== null) { const d = min - baseMin; if (d === 0 && card.dataset.showBase !== 'true') { diff.hidden = true; diff.textContent = ''; } else { diff.textContent = d === 0 ? money(min) : (d > 0 ? '+' : '−') + money(Math.abs(d)); diff.hidden = false; } }
          card.classList.toggle('is-selected', selected[i] === card.dataset.value);
          card.setAttribute('aria-pressed', selected[i] === card.dataset.value ? 'true' : 'false');
        });
        const pick = $('[data-step-pick]', step); if (pick) pick.textContent = selected[i] || '';
      }
      function refreshQtyStep() {
        if (!qtyStep) return;
        const v = findVariant(selected); const unit = v ? v.price : cheapestFor(selected);
        $$('[data-qty]', qtyStep).forEach((card) => {
          const n = Number(card.dataset.qty); const t = $('[data-qty-total]', card); if (t && unit !== null) t.textContent = money(unit * n);
          card.classList.toggle('is-selected', qty === n); card.setAttribute('aria-pressed', qty === n ? 'true' : 'false');
        });
        const pick = $('[data-step-pick]', qtyStep); if (pick) pick.textContent = qty + (qty === 1 ? ' unit' : ' units');
      }
      function refreshSummary() {
        const v = findVariant(selected);
        const complete = selected.every((s) => s !== null);
        if (idInput && v) idInput.value = v.id;
        if (qtyInput) qtyInput.value = qty;
        const unit = v ? v.price : cheapestFor(selected) || 0;
        const total = unit * qty;
        $$('[data-sum-title]', stage).forEach((el) => (el.textContent = v ? v.title : ''));
        $$('[data-sum-unit]', stage).forEach((el) => (el.textContent = money(unit)));
        $$('[data-sum-qty]', stage).forEach((el) => (el.textContent = qty));
        $$('[data-sum-total]', stage).forEach((el) => (el.textContent = money(total)));
        $$('[data-sum-compare]', stage).forEach((el) => { if (v && v.compare_at_price && v.compare_at_price > v.price) { el.textContent = money(v.compare_at_price * qty); el.hidden = false; } else el.hidden = true; });
        $$('[data-sum-sku]', stage).forEach((el) => (el.textContent = v && v.sku ? v.sku : ''));
        const ok = complete && v && v.available;
        [addBtn, buyNow].forEach((b) => { if (!b) return; b.disabled = !ok; b.classList.toggle('is-disabled', !ok); const lbl = $('[data-label]', b) || b; if (complete && v && !v.available) lbl.textContent = b.dataset.soldOut || 'Sold out'; else lbl.textContent = b.dataset.label || lbl.textContent; });
        if (stickyBar) stickyBar.classList.toggle('is-ready', !!ok);
        // variant url
        if (v && history.replaceState) { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u); }
        // gallery filter by color
        if (gallery && colorOption) filterGallery(selected[colorOption - 1]);
        // live render recolor
        if (consoleEl && colorOption && selected[colorOption - 1]) { const sw = $(`[data-value="${CSS.escape(selected[colorOption - 1])}"][data-hex]`, cfg); if (sw) TK.setShell(consoleEl, sw.dataset.hex); }
        document.dispatchEvent(new CustomEvent('tk:config:change', { detail: { variant: v, qty, selected } }));
      }
      function filterGallery(color) {
        const items = $$('[data-gallery-color]', gallery);
        if (!items.length) return;
        const c = (color || '').toLowerCase();
        let shown = 0;
        items.forEach((it) => { const ic = (it.dataset.galleryColor || '').toLowerCase(); const show = !c || !ic || ic === c; it.hidden = !show; if (show) shown++; });
        const active = $('.is-active', gallery); if (active && active.hidden) { const first = items.find((i) => !i.hidden); first && first.click(); }
        $$('[data-gallery-thumbs]', gallery).forEach((t) => (t.hidden = shown === 0));
      }
      // bindings
      optionSteps.forEach((step, i) => {
        $$('[data-value]', step).forEach((card) => on(card, 'click', () => {
          if (card.classList.contains('is-unavailable')) return;
          selected[i] = card.dataset.value;
          for (let k = i + 1; k < selected.length; k++) selected[k] = null;
          optionSteps.forEach((s, j) => refreshOptionStep(j));
          refreshQtyStep(); refreshSummary();
          setTimeout(() => openStep(i + 1, true), 160);
        }));
        on($('[data-step-change]', step), 'click', () => openStep(i, true));
      });
      if (qtyStep) {
        $$('[data-qty]', qtyStep).forEach((card) => on(card, 'click', () => { qty = Number(card.dataset.qty); refreshQtyStep(); refreshSummary(); setTimeout(() => openStep(optionSteps.length + 1, true), 160); }));
        on($('[data-step-change]', qtyStep), 'click', () => openStep(optionSteps.length, true));
        const custom = $('[data-qty-custom]', qtyStep);
        on(custom, 'change', () => { qty = Math.max(1, parseInt(custom.value, 10) || 1); refreshQtyStep(); refreshSummary(); });
      }
      on(form, 'submit', (e) => {
        e.preventDefault();
        const v = findVariant(selected); if (!v || !v.available) { TK.toast('Finish your selection first'); return; }
        cart.add([{ id: v.id, quantity: qty }], { button: addBtn, open: true });
      });
      on(buyNow, 'click', (e) => { e.preventDefault(); const v = findVariant(selected); if (!v || !v.available) return; if (TK.harness) { TK.toast('Harness: checkout'); return; } buyNow.classList.add('is-loading'); fetch(`${TK.routes.cart || '/cart'}/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: v.id, quantity: qty }] }) }).then(() => (window.location.href = TK.routes.checkout || '/checkout')).catch(() => { buyNow.classList.remove('is-loading'); TK.toast('Could not start checkout'); }); });
      // gallery thumbs
      if (gallery) {
        const main = $('[data-gallery-main]', gallery), render = $('[data-gallery-render]', gallery);
        $$('[data-gallery-color]', gallery).forEach((t) => on(t, 'click', () => {
          $$('[data-gallery-color]', gallery).forEach((x) => x.classList.toggle('is-active', x === t));
          if (t.dataset.galleryRender === 'true') { if (render) render.hidden = false; if (main) main.hidden = true; return; }
          if (render) render.hidden = true; if (main) { main.hidden = false; const img = $('img', main); if (img) { img.src = t.dataset.src; img.srcset = t.dataset.srcset || ''; img.alt = t.dataset.alt || ''; } }
        }));
      }
      // preselect from URL (?variant=) or defaults
      const url = new URL(location.href); const vid = Number(url.searchParams.get('variant'));
      const preset = vid ? variants.find((v) => v.id === vid) : null;
      const colorParam = url.searchParams.get('color');
      if (preset) { preset.options.forEach((o, i) => (selected[i] = o)); optionSteps.forEach((s, j) => refreshOptionStep(j)); refreshQtyStep(); refreshSummary(); openStep(optionSteps.length + (qtyStep ? 0 : 0)); if (!qtyStep) openStep(optionSteps.length); }
      else if (colorParam && colorOption) { selected[colorOption - 1] = colorParam; optionSteps.forEach((s, j) => refreshOptionStep(j)); refreshQtyStep(); refreshSummary(); openStep(colorOption); }
      else { optionSteps.forEach((s, j) => refreshOptionStep(j)); refreshQtyStep(); refreshSummary(); openStep(0); }
    });
  }

  /* ---------- sticky buy bar ---------- */
  function initStickyBar() {
    const bar = $('[data-sticky-bar]'); if (!bar) return;
    const anchor = $('[data-add]') || $('[data-sticky-anchor]');
    const cfg = $('[data-tk-config]'), summary = $('[data-summary]'); const update = () => { const vh = window.innerHeight; const offScreen = (el) => { if (!el) return true; const r = el.getBoundingClientRect(); return r.bottom < 0 || r.top > vh; }; let off = offScreen(cfg); if (!off && summary && summary.classList.contains('is-open')) off = offScreen(anchor); bar.classList.toggle('is-visible', off); bar.setAttribute('aria-hidden', off ? 'false' : 'true'); };
    on(window, 'scroll', update, { passive: true }); on(document, 'tk:config:change', () => setTimeout(update, 600)); update();
    on($('[data-sticky-go]', bar), 'click', (e) => { e.preventDefault(); const t = $('[data-tk-config]'); t && t.scrollIntoView({ behavior: MOTION === 'off' ? 'auto' : 'smooth', block: 'start' }); });
  }

  /* ---------- init ---------- */
  function initAll(root) {
    initReveal(root); initCounters(root); initMarquee(root); initFilm(root); initColors(root); initAccordion(root); initTilt(root); initConfigurator(root);
    if (root !== document) cart.bind();
  }
  function boot() {
    initHeader(); initHero(); initCart(); initStickyBar(); initAll(document);
    on(document, 'shopify:section:load', (e) => { initAll(e.target); if (e.target.querySelector('[data-tk-hero]')) initHero(); if (e.target.querySelector('[data-tk-header]')) initHeader(); });
    on(document, 'shopify:block:select', (e) => { const acc = e.target.closest && e.target.closest('.tk-acc__item'); if (acc) { acc.classList.add('is-open'); } const step = e.target.closest && e.target.closest('[data-film-step]'); if (step) step.scrollIntoView({ block: 'center' }); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
